import React from 'react';
import { Row, Col, Form, Input, Switch, Tooltip, Select, Icon } from 'antd';
import { useTranslation } from 'react-i18next';

import UserRoleEnum from '../../../../../constants/UserRoleEnum';
import { hasAccessRole, isCampaignFinished } from '../../../../../services/utils';
import Colors from '../../../../../themes/Colors';

const { TextArea } = Input;
const { Option } = Select;

const CampaignInfoPanel = ({
  errors,
  inputCharLimit,
  areaCharLimit,
  subsidized_all,
  campaignName,
  campaignDescription,
  categoriesId,
  categories,
  isCampaignPaused,
  isCampaignTest,
  editCampaignActive,
  currentCampaign,
  isSuperUser,
  user,
  allDisplaysAreOwn,
  setCampaignName,
  setCampaignDescription,
  changeSubsidizedPrice,
  changeCategory,
  setIsCampaignPaused,
  setIsCampaignTest
}) => {
  const { t, i18n } = useTranslation();

  const hasAccessIsPauseCampaign = allOwn => {
    if (!currentCampaign) return false;
    const isFinished = isCampaignFinished(currentCampaign.end_date);
    if (isFinished) return false;
    return (
      editCampaignActive &&
      currentCampaign.payment_status !== 'pending' &&
      currentCampaign.payment_status !== 'cancelled' &&
      currentCampaign.status !== 'proposal' &&
      user &&
      (hasAccessRole(user.roles, [UserRoleEnum.superAdmin, UserRoleEnum.superModeratorAgency]) ||
        allOwn)
    );
  };

  return (
    <Row gutter={[16, 16]}>
      {/* Nombre */}
      <Col xs={24} sm={24} md={24} lg={8} xl={8}>
        <p style={{ fontSize: '13px' }}>{t('Name')}</p>
        <Form.Item
          validateStatus={
            !!errors['campaignName'] ? 'error' : inputCharLimit.validateStatus ? 'warning' : null
          }
          help={errors['campaignName'] || inputCharLimit.errorMsg}
        >
          <Input
            autoFocus
            placeholder={t('Campaign name placeholder')}
            value={campaignName}
            onChange={e => setCampaignName(e.target.value)}
            maxLength={40}
            autoComplete="off"
          />
        </Form.Item>

        <div className="contentSettingCampaign">
          {hasAccessIsPauseCampaign(allDisplaysAreOwn) && (
            <Form.Item>
              <span style={{ display: 'flex', alignItems: 'center', marginBottom: '10px' }}>
                {t('Pause campaign')}
                &emsp;
                <Switch onChange={setIsCampaignPaused} checked={isCampaignPaused} size="small" />
              </span>
            </Form.Item>
          )}

          {!editCampaignActive &&
            user &&
            (hasAccessRole(user.roles, [
              UserRoleEnum.superAdmin,
              UserRoleEnum.superModeratorAgency
            ]) ||
              allDisplaysAreOwn) && (
              <Form.Item>
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '8px',
                    maxWidth: '300px'
                  }}
                >
                  <span style={{ display: 'flex', alignItems: 'center' }}>
                    {t('Subsidize price')}
                    &emsp;
                    <Switch
                      checked={subsidized_all}
                      onClick={checked => changeSubsidizedPrice(checked)}
                      size="small"
                    />
                    <Tooltip
                      placement="bottomLeft"
                      title={t(
                        'By enabling this option, the campaign is subsidize, your price will be zero'
                      )}
                    >
                      <Icon
                        style={{ fontSize: '17px', marginLeft: '10px', color: Colors.atention }}
                        type="warning"
                      />
                    </Tooltip>
                  </span>
                  {subsidized_all && (
                    <span className="ant-tag ant-tag-orange" style={{ width: 'fit-content' }}>
                      {t('Campaign subsidized')}
                    </span>
                  )}
                </div>
              </Form.Item>
            )}
        </div>
      </Col>

      {/* Categorías */}
      <Col xs={24} sm={24} md={24} lg={8} xl={8}>
        <p style={{ fontSize: '13px' }}>{t('Categories')}</p>
        <Form.Item
          validateStatus={!!errors['categories'] ? 'error' : null}
          help={errors['categories']}
        >
          <Select
            defaultValue={null}
            showSearch
            placeholder={t('Select a category')}
            className="category-select"
            value={categoriesId}
            onChange={changeCategory}
            style={{ width: '100%' }}
            filterOption={(input, option) =>
              option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0
            }
          >
            <Option key="placeholder" value={null} style={{ color: 'gray' }}>
              {t('None')}
            </Option>
            {categories.map(category => (
              <Option key={category.id} value={category.id}>
                {i18n.language === 'en' ? category.name_en : category.name_es}
              </Option>
            ))}
          </Select>
        </Form.Item>

        {isSuperUser && (
          <Form.Item style={{ display: 'flex', flexDirection: 'column' }}>
            <span>
              {t('Is it a test campaign?')}
              &emsp;
              <Switch
                onChange={setIsCampaignTest}
                checked={isCampaignTest}
                size="small"
                disabled={currentCampaign && !currentCampaign.smart_campaign && editCampaignActive}
              />
            </span>
          </Form.Item>
        )}
      </Col>

      {/* Objetivo */}
      <Col xs={24} sm={24} md={24} lg={8} xl={8}>
        <p style={{ fontSize: '13px' }}>{t('Objective')}</p>
        <Form.Item
          validateStatus={areaCharLimit.validateStatus && 'warning'}
          help={errors['campaignDescription'] || areaCharLimit.errorMsg}
        >
          <TextArea
            placeholder={t('Briefly describe the purpose and goals of your campaign.')}
            onChange={e => setCampaignDescription(e.target.value)}
            value={campaignDescription}
            autoSize={{ minRows: 1, maxRows: 6 }}
            maxLength={200}
            className="textArea"
            autoComplete="off"
          />
        </Form.Item>
      </Col>
    </Row>
  );
};

export default CampaignInfoPanel;
